import { z } from 'zod'

/**
 * The design labels this field "Username", but the service authenticates by
 * email (`LoginDto` marks it `@IsEmail()` and looks the user up by `email`).
 * Rather than hard-code a client-side rule that contradicts the label, we only
 * require a non-empty value and let the service be the authority on the format
 * — so if username sign-in is added later, this needs no change.
 */
export const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required.')
    .max(255, 'Username must be at most 255 characters.')
    .transform((value) => value.trim()),
  password: z
    .string()
    .min(1, 'Password is required.')
    .max(255, 'Password must be at most 255 characters.'),
  rememberMe: z.boolean(),
})

export type LoginFormValues = z.input<typeof loginSchema>
export type LoginFormOutput = z.output<typeof loginSchema>

/** Mirrors `ChangePasswordDto` — the new password must be 8–255 characters. */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required.'),
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters.')
      .max(255, 'New password must be at most 255 characters.'),
    confirmPassword: z.string().min(1, 'Confirm your new password.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    message: 'The new password must differ from the current one.',
    path: ['newPassword'],
  })

export type ChangePasswordFormValues = z.input<typeof changePasswordSchema>
